import { useEffect, useMemo, useState } from 'react';
import Layout from '../Layout';
import ModalPasswordRules from '../ModalPasswordRules';
import NewPasswordForm from '../Form/Implementations/PasswordForm';
import PageTemplate from '../PageTemplate';
import RecoveryLayout from './Layout';
import commonStyles from '../styles';
import { createStyles } from '@material-ui/styles';
import { makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { validatePassword } from '../Form/Validation';

interface Props {
  handleSubmit: (email: string) => void;
  error: string;
}

const NewPassword = ({ handleSubmit, error }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { link } = makeStyles((theme) => createStyles(commonStyles(theme)))();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [modalStatus, setModalStatus] = useState(false);
  const [formatError, setFormatError] = useState('');
  const isPasswordValid = Boolean(!password) || validatePassword(password);
  const isConfirmValid = password === passwordConfirm;

  const errors: Record<string, boolean> = useMemo(
    () => ({ 'password.instruction': false, 'password.match': false }),
    []
  );

  useEffect(() => {
    errors['password.instruction'] = Boolean(password && !isPasswordValid);
    errors['password.match'] = Boolean(passwordConfirm && !isConfirmValid);
    let isErrorExist = false;
    for (const err in errors) {
      if (errors[err]) {
        setFormatError(t(err));
        isErrorExist = true;
        break;
      }
    }
    if (!isErrorExist) setFormatError('');
  }, [isPasswordValid, t, isConfirmValid, password, passwordConfirm, errors]);

  return (
    <Layout>
      <RecoveryLayout>
        <PageTemplate
          title={t('auth.restore.password')}
          description={
            <>
              {t('auth.create.password.brief')}
              <a
                className={link}
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  setModalStatus(true);
                }}
              >
                {' '}
                {t('auth.create.password.requirements')}
              </a>
            </>
          }
          form={
            <NewPasswordForm
              password={password}
              passwordConfirm={passwordConfirm}
              setPassword={setPassword}
              setPasswordConfirm={setPasswordConfirm}
              isPasswordValid={isPasswordValid}
              isPasswordConfirmValid={isConfirmValid}
              error={formatError || error}
            />
          }
          submitButtonContent={`${t('auth.save.password')}`}
          submit={() => handleSubmit(password)}
          disableSubmit={Boolean(!password || !passwordConfirm || formatError)}
        />
        <ModalPasswordRules open={modalStatus} setOpen={setModalStatus} />
      </RecoveryLayout>
    </Layout>
  );
};

export default NewPassword;
